import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';

import useApiClient from '../../../../Shared/services/api';
import { useAppContext } from '../../../../Shared/store/appContext';
import { validateFileFormat } from '../../../utils/fileFormatValidation';
import { readFile } from '../../../utils/fileReader';
import { triggerBlobDownload } from '../../../../Shared/utils/fileDownload';
import {
  validateContentHeaders,
  validateAllRows,
} from '../../../utils/fileContentValidation';
import {
  MAX_INLINE_ERROR_COUNT,
  ROW_NUMBER_OFFSET,
  BEAN_VALIDATION_PATTERN,
  BEAN_VALIDATION_PARSE_PATTERN,
  FileUploadErrorCode,
} from '../../../constants/FileUploadConstants';
import { uploadCSV } from '../../../services/service';
import {
  FileUploadErrorResponse,
  FileUploadSuccessResponse,
} from '../../../interfaces/fileUploadInterfaces';
import {
  generateFrontendErrorCSV,
  generateBackendErrorCSV,
} from '../../../utils/errorCSVGenerator';

export const useFileUpload = (type: string) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string>('');
  const [fileName, setFileName] = useState<string>('');
  const [fileData, setFileData] = useState<File>();
  const [parsedData, setParsedData] = useState<string[][]>();
  const [csvErrorData, setCsvErrorData] = useState<{ csvContent: string; fileName: string } | null>(null);
  const api = useApiClient();
  const { state } = useAppContext();
  const selectedArtEventId = state.selectedArtEventId;

  const isBeanValidationError = (message: string): boolean => {
    return BEAN_VALIDATION_PATTERN.test(message || '');
  };

  const parseBackendErrorMessage = (message: string): string[] => {
    if (!message) return [];
    return message.split(', ').map(error => {
      const match = error.match(BEAN_VALIDATION_PARSE_PATTERN);
      if (match) {
        const rowNumber = parseInt(match[1], 10) + ROW_NUMBER_OFFSET;
        return `Row ${rowNumber}: ${match[2]}`;
      }
      return error;
    });
  };

  const parseBackendErrors = (error: FileUploadErrorResponse): string[] => {
    if (error.errorCode === FileUploadErrorCode.CSV_PARSING_ERROR) return [error.message];
    if (error.invalidRows && error.invalidRows.length > 0) return error.invalidRows;
    if (error.errorCode === FileUploadErrorCode.VALIDATION_ERROR || isBeanValidationError(error.message)) {
      return parseBackendErrorMessage(error.message);
    }
    if (error.failedBatches && error.failedBatches.length > 0) {
      return [t('upload-partially-failed-retry-message')];
    }
    return [error.message];
  };

  const uploadFileMutation = useMutation<
    FileUploadSuccessResponse,
    FileUploadErrorResponse,
    File
  >({
    mutationFn: (file: File) => {
      const result = uploadCSV(api, file, type, selectedArtEventId || undefined);
      if (!result) throw new Error('Upload returned no result');
      return result;
    },
    onSuccess: () => {
      setFileName('');
      setFileData(undefined);
      setParsedData(undefined);
      setCsvErrorData(null);
    },
    onError: (error) => {
      const parsedErrors = parseBackendErrors(error);
      if (error.statusCode !== 500 && parsedErrors && parsedErrors.length > MAX_INLINE_ERROR_COUNT && parsedData && fileData) {
        const csvData = generateBackendErrorCSV(parsedData, fileData.name, parsedErrors);
        setCsvErrorData(csvData);
      } else {
        setCsvErrorData(null);
      }
    },
  });

  async function handleFileChange(event: React.SyntheticEvent, files: File[]) {
    setFileData(undefined);
    setFileName('');
    uploadFileMutation.reset();
    setParsedData(undefined);
    setCsvErrorData(null);
    const file = files?.[0];
    setError('');

    if (!file) return;

    const validation = validateFileFormat(file, t);
    if (!validation.isValid) {
      setFileName('');
      setError(validation.error as string);
      return;
    }

    try {
      const data = await readFile(file);
      setParsedData(data);

      const [headerError, headerSuccess] = validateContentHeaders(data[0], type, t);
      if (headerError !== '') {
        setError(headerError);
        setFileName('');
        return;
      }

      const dataValidation = validateAllRows(data, type, t);
      if (!dataValidation.isValid) {
        const duplicateGroupCount = dataValidation.duplicateGroups?.length ?? 0;
        const errorCount = dataValidation.rowErrors.length + duplicateGroupCount;

        if (errorCount > MAX_INLINE_ERROR_COUNT) {
          const csvData = generateFrontendErrorCSV(data, file.name, dataValidation.rowErrors, dataValidation.duplicateGroups, t);
          setCsvErrorData(csvData);
          setError(t('file-validation-failed-download-report'));
          setFileName('');
          return;
        }

        const errorParts: string[] = [];
        if (dataValidation.uniquenessErrors?.length) {
          errorParts.push(...dataValidation.uniquenessErrors);
        }
        if (dataValidation.rowErrors.length > 0) {
          const rowErrorMessages = dataValidation.rowErrors.map((err) =>
            t('file-validation-row-error', { row: err.row, errors: err.errors.join(', ') })
          );
          errorParts.push(...rowErrorMessages);
        }
        errorParts.push(t('file-validation-error-message'));
        setError(errorParts.join('\n'));
        setFileName('');
        return;
      }

      setFileName(file.name + ' ' + headerSuccess);
      setFileData(file);
    } catch (error) {
      setError(error instanceof Error ? error.message : t('file-reading-error-message'));
    }
  }

  const uploadCsvFile = () => {
    if (fileData) uploadFileMutation.mutate(fileData);
  };

  const handleDownloadErrorCSV = () => {
    if (csvErrorData) triggerBlobDownload(csvErrorData.csvContent, csvErrorData.fileName);
  };

  return {
    fileState: { fileName, fileData, error, csvErrorData },
    uploadState: {
      isPending: uploadFileMutation.isPending,
      isError: uploadFileMutation.isError,
      isSuccess: uploadFileMutation.isSuccess,
      error: uploadFileMutation.error,
    },
    selectedArtEventId,
    handleFileChange,
    uploadCsvFile,
    handleDownloadErrorCSV,
    parseBackendErrors,
  };
};
